import type { FC } from 'react'
import { Text, Tag, Flex, GridItem, Grid } from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import { ImgProxy, PLACEHOLDER_SRC, SIZE_410 } from '@yta/image-proxy'
import type {
	LocationResult,
	ProviderResult,
} from '@alexanderathoodly/data-models'
import { displayAddress } from '@alexanderathoodly/data-models'
import {
	PinIcn,
	ContentSection,
	ContentSectionHeading,
} from '@alexanderathoodly/ui-library'
import { Link } from 'react-router-dom'
import { sendGA4Event } from '~/analytics/ga'
import { useTranslation } from 'react-i18next'

interface LocationCardProps {
	location: LocationResult
}

const LocationCard: FC<LocationCardProps & ChakraProps> = ({
	location,
	...rest
}) => {
	const { t } = useTranslation()

	return (
		<GridItem
			as={Link}
			to={`/kontorshotell/${location.url}`}
			role="group"
			onClick={() => {
				sendGA4Event('site_interaction', {
					type: 'navigation',
					sub_type: 'operator_listings',
				})
			}}
			{...rest}
		>
			<ImgProxy
				src={location.images[0] || PLACEHOLDER_SRC}
				sizeSet={{ base: SIZE_410 }}
				width="100%"
				loading="lazy"
				alt={t('kontorshotell i {{city}} - {{location_name}}', {
					ns: 'meta_data',
					city: location.address.city,
					location_name: location.name,
				})}
			/>
			<Text
				fontSize="md"
				color="blue.500"
				mt={4}
				_groupHover={{ textDecoration: 'underline' }}
			>
				{location.name}
			</Text>
			<Flex alignItems="center" mt={1}>
				<PinIcn
					h={4}
					w={4}
					outer={{ fill: 'blue.500', stroke: 'blue.500' }}
					inner={{ fill: 'white' }}
					mr={1}
				/>
				<Text fontSize="sm" color="gray.500">
					{displayAddress(location.address)}
				</Text>
			</Flex>
			{location.highlights.map((highlight) => (
				<Tag
					key={highlight}
					borderRadius="full"
					color="light-blue"
					fontSize="xs"
					mr={1}
					mt={2}
				>
					{t(highlight)}
				</Tag>
			))}
		</GridItem>
	)
}

const LocationsSection: FC<{ provider: ProviderResult }> = ({ provider }) => {
	const { t } = useTranslation()

	return provider.locations.length > 0 ? (
		<ContentSection>
			<ContentSectionHeading mb={{ base: 5, md: 10 }}>
				{provider.name + ' ' + t('Anläggningar').toLowerCase()}
			</ContentSectionHeading>
			<Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={5}>
				{provider.locations.map((location) => (
					<LocationCard key={location.id} location={location} />
				))}
			</Grid>
		</ContentSection>
	) : (
		<></>
	)
}

export default LocationsSection
